import { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { FormControl, Select, Stack, MenuItem } from "@mui/material";

import CircleIcon from "@mui/icons-material/Circle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import { getShippingOrder } from "src/api/shippingOrder";
import { getLalamoveDriverInfo } from "src/api/lalamove";

import device from "src/styles/device";
import font from "src/styles/font";
import { Primary } from "src/styles/color";
import { useDialog } from "src/context/DialogContext";

import Main from "src/components/core/Main";
import Header from "src/components/ui/Header";
import Loading from "src/components/ui/Loading";

import Pending from "src/modules/order/shipping-order/status-order/Pending";
import Confirmed from "src/modules/order/shipping-order/status-order/Confirmed";
import Preparing from "src/modules/order/shipping-order/status-order/Preparing";
import Delivering from "src/modules/order/shipping-order/status-order/Delivering";
import Shipped from "src/modules/order/shipping-order/status-order/Shipped";
import Failed from "src/modules/order/shipping-order/status-order/Failed";
import ConfirmDialog from "src/modules/order/shipping-order/modal/ConfirmDialogDup";
import DeliveryFailedDialog from "src/modules/order/shipping-order/modal/DeliveryFailedDialog";
import PreparingDialog from "src/modules/order/shipping-order/modal/PreparingDialog";
import ShippedDialog from "src/modules/order/shipping-order/modal/ShippedDialog";
import DeliveringDialog from "src/modules/order/shipping-order/modal/DeliveringDialog";
import ShippingStepper from "src/modules/order/shipping-order/status-order/Stepper";

const StyledSelect = styled(Select)`
  border-radius: 16px;
  text-align: center;

  .MuiSelect-select {
    padding: 8px 16px;
  }
  .MuiOutlinedInput-notchedOutline,
  &.MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${Primary};
    border-width: 1.5px;
  }
  .Mui-disabled,
  &.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${Primary};
  }

  @media ${device.tabletL} {
    .MuiSelect-select {
      padding: 8px 12px;
    }
  }
`;

const StyledMenuItem = styled(MenuItem)`
  &.MuiMenuItem-root {
    line-height: 24px;
    min-height: 40px;
    padding: 8px 24px;
  }
`;

const SelectSpan = styled.span`
  font-size: ${font.size.sm};
  font-weight: 400;
  line-height: 24px;

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

const LabelAction = styled.div`
  padding: 4px 8px;
  height: 24px;
  border-radius: 8px;
  font-size: 0.75rem;
  color: #ffffff;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ isAdmin }) => (isAdmin ? "#005fcc" : "#bf0013")};

  @media ${device.mobileL} {
    display: none;
  }
`;

const LabelEditStatus = styled.div`
  font-size: 0.875rem;
  text-decoration: underline;
  height: inherit;
  display: flex;
  align-items: center;
  font-weight: 400;

  @media ${device.tabletL} {
    display: none;
  }
`;

const ShippingDetail = ({ open }) => {
  const { orderId } = useParams();

  const initialDetail = {
    order: {},
    method: "ส่งไปถึงลูกค้าโดยตรง",
    fulfillmentProcess: [
      {
        type: "มีค่าจัดส่ง",
        carrier: "จัดส่งโดยร้านค้า",
        trackingNumber: null,
        trackingUrl: null,
        shippingFee: null,
        pickupAddress: {
          name: null,
          tel: null,
          address: null,
          coordinates: {
            lat: 13.6940782,
            lng: 100.6022862,
          },
        },
        shippingProofs: [],
        deliveryProofs: [],
        shippedDate: null,
        estimatedDeliveryDate: null,
        actualDeliveryDate: null,
        remark: null,
      },
    ],
    payment: {
      method: null,
      info: {
        bankAccountName: null,
        bankNo: null,
      },
      total: null,
      paymentDate: null,
      paymentProof: null,
    },
    status: {
      failureReason: null,
      value: "pending",
    },
    note: null,
    documents: {
      customer: {
        qt: {
          qtDate: null,
          qtNo: null,
          customerQtFile: null,
          customerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          customerInvFile: null,
          customerInvAdditionalFile: null,
        },
      },
      xsurface: {
        po: {
          poDate: null,
          poNo: null,
          xsurfacePoFile: null,
          xsurfacePoAdditionalFile: null,
        },
        vc: {
          vcDate: null,
          vcNo: null,
          xsurfaceVcFile: null,
          xsurfaceVcAdditionalFile: null,
        },
      },
      seller: {
        qt: {
          qtDate: null,
          qtNo: null,
          sellerQtFile: null,
          sellerQtAdditionalFile: null,
        },
        inv: {
          invDate: null,
          invNo: null,
          sellerInvFile: null,
          sellerInvAdditionalFile: null,
        },
      },
    },
  };

  const [orderDetail, setOrderDetail] = useState(initialDetail);

  const [statusActive, setStatusActive] = useState("pending");
  const lalamovePendingStatus = ["ASSIGNING_DRIVER", "ON_GOING", "PICKED_UP"];

  const { openDialog, closeDialog, closeAllDialogs } = useDialog();

  const [isLoading, setIsLoading] = useState(false);

  const statusList = [
    {
      label: "รอจัดส่ง",
      value: "pending",
      color: "#FBB634",
      showingStatus: ["confirmed", "failed"],
    },
    {
      label:
        statusActive === "confirmed"
          ? "รอชำระค่าจัดส่ง"
          : "เลือกช่องทางการจัดส่ง",
      value: "confirmed",
      color: "#52A8E7",
      showingStatus: ["preparing", "failed"],
    },
    {
      label: "เตรียมจัดส่ง",
      value: "preparing",
      color: "#9A66ED",
      showingStatus: ["delivering", "failed"],
    },
    {
      label: statusActive === "preparing" ? "ดำเนินการจัดส่ง" : "กำลังส่ง",
      value: "delivering", // ?
      color: "#EE7608",
      showingStatus: ["shipped", "failed"],
    },
    {
      label: statusActive === "delivering" ? "ส่งสินค้า" : "รอยืนยันรับสินค้า",
      value: "shipped",
      color: "#219653",
      showingStatus: [],
    },
    {
      label: "จัดส่งสำเร็จ",
      value: "delivered",
      color: "#219653",
      showingStatus: [],
    },
    {
      label: "ส่งไม่สำเร็จ",
      value: "failed",
      color: "#BF0013",
      showingStatus: [],
    },
  ];

  useEffect(() => {
    if (orderId === undefined) return;

    fetchOrderDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      setIsLoading(true);
      const {
        data: { data },
      } = await getShippingOrder(orderId);

      let formatData = { ...data };

      // Check if the order has a lalamove request detail and fetch driver info if available
      if (
        !!data?.fulfillmentProcess[0]?.lalamoveRequest?.result?.driverId &&
        (data?.status.value === "delivering" ||
          data?.status.value === "preparing")
      ) {
        let fulfillmentProcess = data?.fulfillmentProcess[0];
        const {
          data: { data: res },
        } = await getLalamoveDriverInfo(
          data?.fulfillmentProcess[0]?.lalamoveRequest?.result?.orderId,
          data?.fulfillmentProcess[0]?.lalamoveRequest?.result?.driverId,
        );

        if (!!res) {
          fulfillmentProcess.lalamoveRequest.result.driverDetail = res;

          formatData = { ...data, fulfillmentProcess: [fulfillmentProcess] };
        }
      }

      setOrderDetail((prevState) => ({ ...prevState, ...formatData }));
      setStatusActive(formatData.status.value);
    } catch (error) {
      console.error("Error fetching order detail:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const ShippingOrder = () => {
    switch (statusActive) {
      case "pending":
        return (
          <Pending
            info={orderDetail}
            callbackCloseDocument={() => fetchOrderDetail()}
            setIsLoading={setIsLoading}
          />
        );
      case "confirmed":
        return (
          <Confirmed
            info={orderDetail}
            callbackCloseDocument={() => fetchOrderDetail()}
            setIsLoading={setIsLoading}
          />
        );
      case "preparing":
        return (
          <Preparing
            info={orderDetail}
            callbackCloseDocument={() => fetchOrderDetail()}
            setIsLoading={setIsLoading}
          />
        );
      case "delivering":
        return (
          <Delivering
            info={orderDetail}
            callbackCloseDocument={() => fetchOrderDetail()}
            setIsLoading={setIsLoading}
          />
        );
      case "shipped":
        return (
          <Shipped
            info={orderDetail}
            callbackCloseDocument={() => fetchOrderDetail()}
            setIsLoading={setIsLoading}
          />
        );
      case "delivered":
        return (
          <Shipped
            info={orderDetail}
            callbackCloseDocument={() => fetchOrderDetail()}
            setIsLoading={setIsLoading}
          />
        );
      case "failed":
        return (
          <Failed
            info={orderDetail}
            callbackCloseDocument={() => fetchOrderDetail()}
            setIsLoading={setIsLoading}
          />
        );
      default:
        break;
    }
  };

  const handleStatus = ({ target }) => {
    let selectedStatus = target.value;

    if (selectedStatus === "confirmed") {
      openDialog({
        type: "custom",
        customDialog: (
          <ConfirmDialog
            orderDetail={orderDetail}
            onClose={() => closeDialog()}
            setIsLoading={setIsLoading}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
          />
        ),
      });
    } else if (selectedStatus === "preparing") {
      openDialog({
        type: "custom",
        customDialog: (
          <PreparingDialog
            orderDetail={orderDetail}
            onClose={() => closeDialog()}
            setIsLoading={setIsLoading}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
          />
        ),
      });
    } else if (selectedStatus === "delivering") {
      openDialog({
        type: "custom",
        customDialog: (
          <DeliveringDialog
            orderDetail={orderDetail}
            onClose={() => closeDialog()}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
            setIsLoading={setIsLoading}
          />
        ),
      });
    } else if (selectedStatus === "shipped") {
      openDialog({
        type: "custom",
        customDialog: (
          <ShippedDialog
            orderDetail={orderDetail}
            onClose={() => closeDialog()}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
            setIsLoading={setIsLoading}
          />
        ),
      });
    } else if (selectedStatus === "failed") {
      openDialog({
        type: "custom",
        customDialog: (
          <DeliveryFailedDialog
            orderDetail={orderDetail}
            onClose={() => closeDialog()}
            onSuccess={() => {
              closeAllDialogs();
              fetchOrderDetail();
            }}
            setIsLoading={setIsLoading}
          />
        ),
      });
    }
  };

  const handleLabelAction = () => {
    switch (statusActive) {
      case "shipped":
      case "failed":
        return <></>;
      case "confirmed":
        return (
          <LabelAction isAdmin>
            {"สถานะการจัดส่งนี้ แอดมิน เป็นผู้ดำเนินการ"}
          </LabelAction>
        );
      default:
        return (
          <LabelAction>
            {'สถานะการจัดส่งนี้ "ผู้ขาย" เป็นผู้ดำเนินการ'}
          </LabelAction>
        );
    }
  };

  const handleLabeSubHeader = () => {
    switch (statusActive) {
      case "pending":
        return 'สถานะ "รอจัดส่ง" ใช้สำหรับผู้ขายในการตรวจสอบข้อมูลลูกค้าและคำสั่งซื้อ พร้อมทั้งแก้ไขสถานะการจัดส่งได้ที่มุมขวาบนของหน้าจอ';
      case "confirmed":
        return 'สถานะ "ยืนยันการจัดส่ง" ใช้สำหรับแอดมินเพื่อตรวจสอบข้อมูลลูกค้าและคำสั่งซื้อ พร้อมแก้ไขสถานะการจัดส่งที่มุมขวาบนของหน้าจอ';
      case "preparing":
        return 'สถานะ "เตรียมจัดส่ง" ใช้สำหรับผู้ขายในการตรวจสอบข้อมูลลูกค้าและคำสั่งซื้อ พร้อมทั้งแก้ไขสถานะการจัดส่งได้ที่มุมขวาบนของหน้าจอ';
      case "delivering":
        return 'สถานะ "กำลังจัดส่ง" ใช้สำหรับผู้ขายในการตรวจสอบข้อมูลลูกค้าและคำสั่งซื้อ พร้อมทั้งแก้ไขสถานะการจัดส่งได้ที่มุมขวาบนของหน้าจอ';
      default:
        return "";
    }
  };

  return (
    <Main open={open} paddingUnset>
      <Header
        title="รายละเอียดการจัดส่ง"
        label={handleLabelAction()}
        isSubHeader={statusActive !== "shipped" && statusActive !== "failed"}
        isAdmin={statusActive === "confirmed"}
        subHeader={handleLabeSubHeader()}
      >
        {statusActive !== "shipped" &&
          statusActive !== "failed" &&
          statusActive !== "confirmed" && (
            <LabelEditStatus>
              แก้ไขสถานะการจัดส่งได้ที่นี่
              <ArrowForwardIosIcon sx={{ fontSize: 15, marginLeft: 1 }} />
            </LabelEditStatus>
          )}

        <FormControl
          sx={{
            minWidth: 120,
            alignSelf: "flex-end",
          }}
        >
          <StyledSelect
            labelId="status-select-label"
            id="status-select"
            value={statusActive}
            disabled={
              statusActive === "confirmed" ||
              statusActive === "shipped" ||
              statusActive === "failed"
            }
            onChange={handleStatus}
            sx={{
              "&.MuiInputBase-root": {
                height: "32px",
                fontSize: "0.75rem",
                span: {
                  fontSize: "0.75rem",
                },
                xs: {
                  width: "157px",
                },
                sm: {
                  width: "178px",
                },
                lg: {
                  height: "40px",
                  fontSize: "0.875rem",
                  span: {
                    fontSize: "0.875rem",
                  },
                },
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: 4,
                  ".MuiList-root": {
                    padding: 0,
                  },
                },
              },
            }}
            IconComponent={(props) => {
              if (statusActive !== "shipped" && statusActive !== "failed")
                return (
                  <KeyboardArrowDownIcon
                    {...props}
                    style={{ color: Primary }}
                  />
                );
            }}
          >
            {Object.keys(orderDetail).length > 0 &&
              statusList.map((option) => {
                let currentStatus = statusList.find(
                  ({ value }) => value === orderDetail?.status.value,
                );

                return (
                  <StyledMenuItem
                    value={option.value}
                    style={
                      !currentStatus?.showingStatus?.includes(option.value)
                        ? { display: "none" }
                        : {}
                    }
                    disabled={lalamovePendingStatus.includes(
                      orderDetail?.fulfillmentProcess?.[0]?.lalamoveRequest
                        ?.result?.status,
                    )}
                  >
                    <Stack gap={1} direction="row" alignItems="center">
                      <CircleIcon
                        fontSize={font.size.sm}
                        sx={{ color: option.color }}
                      />
                      <SelectSpan>{option.label}</SelectSpan>
                    </Stack>
                  </StyledMenuItem>
                );
              })}
          </StyledSelect>
        </FormControl>
      </Header>

      <ShippingStepper status={statusActive} />

      <ShippingOrder />

      {isLoading && <Loading />}
    </Main>
  );
};

export default ShippingDetail;
